import axios from "axios";
import "./Notification.css";
import { toast } from "react-toastify";
import { Link, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import DateConverter from "../../helpers/dateFormatChange";
import { logout, reset } from "../../features/auth/authSlice";
import { reset as invoiceReset } from "../../features/invoice/invoiceSlice.js";
import { setNotification } from "../../features/notification/notificationSlice.js";
import { reset as headerReset } from "../../features/headerText/headerTextSlice.js";
import { resetAccountDetails } from "../../features/accountDetails/accountDetailsSlice.js";
import { reset as appSettingsReset } from "../../features/appSettings/appSettingsSlice.js";
import { ReactComponent as CheckmarkImage } from "../../images/Notification-images/check-circle-notification.svg";
import { ReactComponent as ChecktickmarkImage } from "../../images/icons/tick-blue.svg";
import { ReactComponent as ArchiveIcon } from "../../images/Notification-images/archive.svg";
import userTypeConsts from "../../constants/userTypeConsts.js";

// Notification Item Skeleton Loader
const NotificationItemSkeleton = () => (
  <div className="notification-content" style={{ width: "100%" }}>
    <div className="notification-text">
      <div
        style={{
          width: "85%",
          height: "16px",
          backgroundColor: "#e5e7eb",
          borderRadius: "4px",
          animation: "pulse 1.5s ease-in-out infinite",
          marginBottom: "8px",
        }}
      />
      <div
        style={{
          width: "60%",
          height: "16px",
          backgroundColor: "#e5e7eb",
          borderRadius: "4px",
          animation: "pulse 1.5s ease-in-out infinite",
        }}
      />
    </div>
    <div className="notification-date">
      <div
        style={{
          width: "80px",
          height: "14px",
          backgroundColor: "#e5e7eb",
          borderRadius: "4px",
          animation: "pulse 1.5s ease-in-out infinite",
        }}
      />
    </div>
  </div>
);

// Notification Action Skeleton Loader (for checkmark area)
const NotificationActionSkeleton = () => (
  <div className="not-outer">
    <div
      style={{
        width: "20px",
        height: "20px",
        backgroundColor: "#e5e7eb",
        borderRadius: "4px",
        animation: "pulse 1.5s ease-in-out infinite",
      }}
    />
  </div>
);

// Button Skeleton Loader
const ButtonSkeleton = () => (
  <div
    style={{
      width: "140px",
      height: "32px",
      backgroundColor: "#e5e7eb",
      borderRadius: "4px",
      animation: "pulse 1.5s ease-in-out infinite",
    }}
  />
);

const TOAST_CONFIG = {
  position: "bottom-right",
  autoClose: 1500,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
  progress: undefined,
  theme: "light",
};

const Notification = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const isFirstLoad = useRef(true);
  const [isReadLoading, setIsReadLoading] = useState(false);
  const [isClickedIndex, setIsClickedIndex] = useState(null);
  const [notificationData, setNotificationData] = useState({ notifications: [] });
  const [isMarkingAllAsRead, setIsMarkingAllAsRead] = useState(false);
  const [readStatus, setReadStatus] = useState(false);
  const [pendingReadIds, setPendingReadIds] = useState(new Set());
  
  const notification = useSelector(
    (state) => state.notification.notificationState
  );
  const { appSettings } = useSelector((state) => state.appSettings);
  const user = useSelector((state) => state.auth.user);
  const notificationRef = useRef(null);

  const userId = useMemo(() => user?._id || null, [user?._id]);

  // Check if user is admin or super admin
  const isAdminUser = useMemo(() => {
    return user?.userType === userTypeConsts.SUPER_ADMIN || user?.userType === userTypeConsts.ADMIN;
  }, [user?.userType]);

  const handleUserDeleted = useCallback(() => {
    localStorage.removeItem("user");
    dispatch(logout());
    dispatch(reset());
    dispatch(invoiceReset());
    dispatch(headerReset());
    dispatch(appSettingsReset());
    toast.error("This user is deleted!", TOAST_CONFIG);
    navigate("/");
  }, [dispatch, navigate]);

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
  }, []);

  const toggleDropdown = useCallback(() => {
    setIsOpen((prev) => !prev);
    dispatch(resetAccountDetails());
    dispatch(setNotification(Date.now()));
  }, [dispatch]);

  const getNotificationMessage = useCallback(
    (item) => {
      if (!item?.notification_data?.[0]) return "";
      if (isAdminUser) {
        return (
          item.notification_data[0].message ||
          item.notification_data[0].user_message ||
          ""
        );
      }
      return (
        item.notification_data[0].user_message ||
        item.notification_data[0].message ||
        ""
      );
    },
    [isAdminUser]
  );

  useEffect(() => {
    const handleBodyClick = (event) => {
      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.body.addEventListener("click", handleBodyClick);
    }

    return () => {
      document.body.removeEventListener("click", handleBodyClick);
    };
  }, [isOpen]);

  useEffect(() => {
    document.body.classList.toggle("open-menu-mob", isOpen);
    return () => {
      document.body.classList.remove("open-menu-mob");
    };
  }, [isOpen]);

  useEffect(() => {
    setReadStatus(false);
  }, [notification]);

  useEffect(() => {
    if (!userId || !user?.token) return;

    const fetchNotificationData = async () => {
      try {
        if (isFirstLoad.current) setIsLoading(true);
        
        // Ensure we're using the correct user ID (from user object, not from separate userId)
        const requestUserId = user?._id || userId;
        
        const response = await axios.post(
          "/api/notification/unread/count",
          { user_id: requestUserId },
          {
            headers: {
              Authorization: `Bearer ${user.token}`,
            },
          }
        );
        
        // Check if response structure is different
        if (response.data && !response.data.notifications && Array.isArray(response.data)) {
          setNotificationData({ notifications: response.data, status: "success", count: response.data.length });
        } else if (response.data && response.data.notifications) {
          setNotificationData(response.data);
        } else {
          setNotificationData(response.data || { notifications: [] });
        }
      } catch (error) {
        console.error(error);
        setNotificationData({ notifications: [], status: "error", count: 0 });
        
        global.config.slackMessage(error.toString());
        if (error.response?.data?.status === "deleted") {
          handleUserDeleted();
          return;
        }
      } finally {
        if (isFirstLoad.current) {
          setIsLoading(false);
          isFirstLoad.current = false;
        }
      }
    };

    fetchNotificationData();
  }, [notification, userId, user?.token, handleUserDeleted]);

  const deleteNotifi = async (event, notificationId, userId, url) => {
    event.stopPropagation();
    setIsClickedIndex(notificationId);
    try {
      await axios.post(
        "/api/notification/notifideleteat",
        {
          user_id: userId,
          notification_id: notificationId,
          url,
        },
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );
      setIsOpen(false);
      dispatch(setNotification(Date.now()));
    } catch (error) {
      console.error(error);
      global.config.slackMessage(error.toString());
      if (error.response?.data?.status === "deleted") {
        handleUserDeleted();
        return;
      }
    } finally {
      setIsClickedIndex(null);
    }
  };

  const readNotifi = (event, notificationId, userId, url) => {
    event.stopPropagation();
    // Optimistic — mark read instantly and close
    setNotificationData((prevState) => ({
      ...prevState,
      notifications: (prevState?.notifications || []).map((item) =>
        item._id === notificationId ? { ...item, read: true } : item
      ),
    }));
    setIsOpen(false);
    // Background API
    axios.post(
      "/api/notification/updatereadat",
      { user_id: userId, notification_id: notificationId, url },
      { headers: { Authorization: `Bearer ${user.token}` } }
    ).then(() => {
      dispatch(setNotification(Date.now()));
    }).catch((error) => {
      console.error(error);
      global.config.slackMessage(error.toString());
      if (error.response?.data?.status === "deleted") handleUserDeleted();
    });
  };

  const readNotification = (event, notificationId, userId, url) => {
    event.stopPropagation();
    // Show checked circle immediately, remove from list after short delay
    setPendingReadIds((prev) => new Set([...prev, notificationId]));
    setTimeout(() => {
      setNotificationData((prevState) => ({
        ...prevState,
        notifications: (prevState?.notifications || []).map((item) =>
          item._id === notificationId ? { ...item, read: true } : item
        ),
      }));
      setPendingReadIds((prev) => { const next = new Set(prev); next.delete(notificationId); return next; });
    }, 500);
    // Background API
    axios.post(
      "/api/notification/updatereadat",
      { user_id: userId, notification_id: notificationId, url },
      { headers: { Authorization: `Bearer ${user.token}` } }
    ).then(() => {
      dispatch(setNotification(Date.now()));
    }).catch((error) => {
      setPendingReadIds((prev) => { const next = new Set(prev); next.delete(notificationId); return next; });
      console.error(error);
      global.config.slackMessage(error.toString());
      if (error.response?.data?.status === "deleted") handleUserDeleted();
    });
  };

  const markAllAsRead = async () => {
    const unread = (notificationData.notifications || []).filter((n) => !(n.read === true || n.read === "true" || n.read === 1));
    if (!unread.length) return;

    const notificationsToMarkAsRead = unread.map((n) => ({
      user_id: userId,
      notification_id: n._id,
      url: n.url,
    }));

    // Optimistic — mark all read instantly
    setNotificationData((prevState) => ({
      ...prevState,
      notifications: (prevState?.notifications || []).map((item) => ({ ...item, read: true })),
    }));
    setIsMarkingAllAsRead(true);

    try {
      const batchSize = 200;
      const total = notificationsToMarkAsRead.length;
      if (total > batchSize) {
        for (let i = 0; i < total; i += batchSize) {
          await axios.post(
            "/api/notification/markallasread",
            { notifications: notificationsToMarkAsRead.slice(i, i + batchSize) },
            { headers: { Authorization: `Bearer ${user.token}` } }
          );
        }
      } else {
        await axios.post(
          "/api/notification/markallasread",
          { notifications: notificationsToMarkAsRead },
          { headers: { Authorization: `Bearer ${user.token}` } }
        );
      }
      toast.success("All notifications marked as read successfully!", TOAST_CONFIG);
      dispatch(setNotification(Date.now()));
    } catch (error) {
      // Revert on failure
      setNotificationData((prevState) => ({
        ...prevState,
        notifications: (prevState?.notifications || []).map((item) => {
          const wasUnread = unread.find((u) => u._id === item._id);
          return wasUnread ? { ...item, read: false } : item;
        }),
      }));
      console.error(error);
      global.config.slackMessage(error.toString());
      toast.error("Error marking all notifications as read", TOAST_CONFIG);
    } finally {
      setIsMarkingAllAsRead(false);
    }
  };

  const unReadNotification = (event, notificationId, userId, url) => {
    event.stopPropagation();
    // Optimistic update — instant UI change
    setNotificationData((prevState) => ({
      ...prevState,
      notifications: (prevState?.notifications || []).map((item) =>
        item._id === notificationId ? { ...item, read: false } : item
      ),
    }));
    // Background API — no await, no spinner
    axios.post(
      "/api/notification/deleteReadNotification",
      { user_id: userId, notification_id: notificationId, url },
      { headers: { Authorization: `Bearer ${user.token}` } }
    ).then(() => {
      dispatch(setNotification(Date.now()));
    }).catch((error) => {
      // Revert on failure
      setNotificationData((prevState) => ({
        ...prevState,
        notifications: (prevState?.notifications || []).map((item) =>
          item._id === notificationId ? { ...item, read: true } : item
        ),
      }));
      console.error(error);
      global.config.slackMessage(error.toString());
      if (error.response?.data?.status === "deleted") handleUserDeleted();
    });
  };

  const filteredData = useMemo(() => {
    if (!notificationData || !notificationData.notifications || !Array.isArray(notificationData.notifications)) {
      return [];
    }

    if (notificationData.notifications.length === 0) {
      return [];
    }

    // Normalize read status - handle string "true"/"false" or boolean
    const normalizedReadStatus = readStatus === true || readStatus === "true";

    return notificationData.notifications.filter((item) => {
      // Normalize item.read - handle string "true"/"false", boolean, or undefined
      const itemRead = item.read === true || item.read === "true" || item.read === 1;
      return itemRead === normalizedReadStatus;
    });
  }, [notificationData, readStatus]);

  const unreadCount = useMemo(() => {
    if (!notificationData || !notificationData.notifications || !Array.isArray(notificationData.notifications)) {
      return 0;
    }
    const count = notificationData.notifications.filter(
      (item) => !(item.read === true || item.read === "true" || item.read === 1)
    ).length;
    return count;
  }, [notificationData]);

  // Only block UI during "Mark All As Read" — individual read/unread are optimistic
  const isAnyProcessing = useMemo(() => {
    return isMarkingAllAsRead;
  }, [isMarkingAllAsRead]);

  const changeReadStatus = useCallback(() => {
    setReadStatus((prev) => !prev);
  }, []);

  return (
    <div className="notification-container" ref={notificationRef}>
      <div className="notification-icon" onClick={toggleDropdown}>
        <img
          src={
            require("../../images/Notification-images/notification-icon_Mode.svg")
              .default
          }
          alt="Notification Icon"
        />
        {unreadCount > 0 && <span className="count">{unreadCount}</span>}
      </div>
      {isOpen && (
        <div
          className={`notification-dropdown notification-dropdown-enter ${
            filteredData.length === 0 ? "no-notifications" : ""
          }`}
        >
          <div className="notification-header-row">
            <h3 className="main-notification-heading">
              {global.config.locate(appSettings?.language, "Notifications")}
            </h3>
            <button
              type="button"
              className="notification-close-mobile"
              aria-label="Close notifications"
              onClick={closeDropdown}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <path
                  d="M1 1L13 13M13 1L1 13"
                  stroke="#282828"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>

          <div
            className={`noti-top-sec ${
              filteredData.length === 0 ? "disabled-noti" : ""
            }`}
          >
            <div
              className={readStatus ? "archive-sec active" : "archive-sec"}
              onClick={changeReadStatus}
            >
              <span className="icon-archive">
                <ArchiveIcon className="archive-img" aria-hidden="true" />
              </span>
              Archive
            </div>

            {!readStatus && (
              <button
                className="noti-read-all"
                onClick={markAllAsRead}
                disabled={isMarkingAllAsRead || filteredData.length === 0}
              >
                {isMarkingAllAsRead ? (
                  <ButtonSkeleton />
                ) : (
                  <>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="11"
                      viewBox="0 0 14 11"
                      fill="none"
                    >
                      <path
                        d="M1 4.78947L5.13793 9L13 1"
                        stroke="#405BFF"
                        strokeOpacity="1"
                        strokeWidth="2"
                      />
                    </svg>
                    {global.config.locate(
                      appSettings?.language,
                      "Mark_All_As_Read"
                    )}
                  </>
                )}
              </button>
            )}
          </div>

          <div className="notification-divider" />

          <ul>
            {isLoading ? (
              [1, 2, 3].map((i) => (
                <li className="notification-listout" key={`skel-${i}`}>
                  <NotificationItemSkeleton />
                  <NotificationActionSkeleton />
                </li>
              ))
            ) : filteredData.length > 0 ? (
              filteredData.map((item) => (
                <li
                  className={`notification-listout ${item.read ? "" : "unread"}`}
                  key={item._id}
                >
                  {isLoading && isClickedIndex === item._id ? (
                    <>
                      <NotificationItemSkeleton />
                      <NotificationActionSkeleton />
                    </>
                  ) : isReadLoading && isClickedIndex === item._id ? (
                    <>
                      <NotificationItemSkeleton />
                      <NotificationActionSkeleton />
                    </>
                  ) : (
                    <>
                      <Link
                        to={item.url}
                        onClick={
                          !item.read
                            ? (e) =>
                                readNotifi(e, item._id, item.user_id, item.url)
                            : null
                        }
                      >
                        <div className="notification-content">
                          <div className="notification-text">
                            {getNotificationMessage(item)}
                          </div>
                          <div className="notification-date">
                            <DateConverter date={item.created_at} />
                          </div>
                        </div>
                      </Link>
                      <div
                        className="not-outer"
                        style={{
                          pointerEvents: isAnyProcessing ? "none" : "auto",
                          opacity: isAnyProcessing ? 0.5 : 1,
                          cursor: isAnyProcessing ? "not-allowed" : "pointer",
                        }}
                      >
                        {(item.read || pendingReadIds.has(item._id)) ? (
                          <div className="tooltip1">
                            <span className="tooltiptext">
                              {global.config.locate(
                                appSettings?.language,
                                "Mark_as_Unread"
                              )}
                            </span>
                            <ChecktickmarkImage
                              className="checkmark"
                              aria-label="Check Mark"
                              onClick={(e) => {
                                if (!isAnyProcessing && !pendingReadIds.has(item._id)) {
                                  unReadNotification(e, item._id, item.user_id, item.url);
                                }
                              }}
                              style={{ cursor: pendingReadIds.has(item._id) ? "default" : isAnyProcessing ? "not-allowed" : "pointer", opacity: pendingReadIds.has(item._id) ? 0.6 : 1, transition: "opacity 0.2s" }}
                            />
                          </div>
                        ) : (
                          <div className="tooltip1">
                            <span className="tooltiptext">
                              {global.config.locate(
                                appSettings?.language,
                                "Mark_as_Read"
                              )}
                            </span>
                            <CheckmarkImage
                              className="checkmark"
                              aria-label="Check Mark"
                              onClick={(e) => {
                                if (!isAnyProcessing) {
                                  readNotification(e, item._id, item.user_id, item.url);
                                }
                              }}
                              style={{ cursor: isAnyProcessing ? "not-allowed" : "pointer" }}
                            />
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </li>
              ))
            ) : (
              <li className="notification-empty">
                <h3>All clear!</h3>
                <p>You have no new notifications.</p>
              </li>
            )}

          </ul>
        </div>
      )}
      <style>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
      `}</style>
    </div>
  );
};

export default Notification;